import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';

interface ResetPasswordFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSuccess, onCancel }) => {
  const { resetPassword, error } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }

    setFormError(null);
    setLoading(true);

    try {
      await resetPassword(email);
      setSuccessMessage('Password reset email sent! Check your inbox for further instructions.');
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by the auth context
      console.error('Password reset error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-center mb-6 text-dark">Reset Password</h2>

      {/* Error message */}
      {(formError || error) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {formError || error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {!successMessage ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-gray-600 mb-4">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </p>

          {/* Email field */}
          <div>
            <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Submit button */}
          <button
            type="submit"
            className={`w-full py-2 px-4 ${
              loading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
            } text-white rounded-lg transition-colors`}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="inline-block animate-spin mr-2">↻</span>
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>

          {/* Back to sign in link */}
          <div className="text-center mt-4">
            <Link to="/sign-in" className="text-primary hover:text-primary-dark">
              Back to Sign In
            </Link>
          </div>

          {/* Cancel button (for modal use) */}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors mt-2"
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </form>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-600 mb-4">
            Check your email for a link to reset your password. If it doesn&apos;t appear within a
            few minutes, check your spam folder.
          </p>

          {/* Back to sign in button */}
          <Link
            to="/sign-in"
            className="block w-full py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-center"
          >
            Back to Sign In
          </Link>

          {/* Resend button */}
          <button
            type="button"
            onClick={handleSubmit}
            className="w-full py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Resend Email'}
          </button>

          {/* Cancel button (for modal use) */}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              disabled={loading}
            >
              Close
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ResetPasswordForm;
